import React, { useCallback, useEffect, useRef } from 'react';
import {
    ThumbnailCanvasWrapper,
    ThumbnailContainer,
    ThumbnailNumberWrapper,
    ThumbnailWrapper,
} from '@components/pdf-reader/styled.ts';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';

interface Props {
    pdf: PDFDocumentProxy;
    pageNumber: number;
    isSelected: boolean;
    onClick: (page: number) => void;
    scale?: number;
}

export const Thumbnail: React.FC<Props> = ({ pdf, pageNumber, scale = 0.5, isSelected, onClick }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const renderTaskRef = useRef<ReturnType<PDFPageProxy['render']> | null>(null);

    const cancelRender = useCallback(() => {
        renderTaskRef.current?.cancel();
        renderTaskRef.current = null;
    }, []);

    const drawThumbnail = useCallback(async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        try {
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale });

            canvas.width = viewport.width;
            canvas.height = viewport.height;
            const canvasContext = canvas.getContext('2d')!;

            cancelRender();
            renderTaskRef.current = page.render({ canvas, canvasContext, viewport });
            await renderTaskRef.current.promise;
        } catch (err: any) {
            if (err?.name !== 'RenderingCancelledException' && !/Cannot use the same canvas/.test(err.message)) {
                console.error('Thumbnail render error:', err);
            }
        }
    }, [pdf, pageNumber, scale, cancelRender]);

    useEffect(() => {
        void drawThumbnail();
        return cancelRender;
    }, [drawThumbnail, cancelRender]);

    const handleClick = useCallback(() => onClick(pageNumber), [onClick, pageNumber]);

    return (
        <ThumbnailWrapper onClick={handleClick}>
            <ThumbnailContainer $isSelected={isSelected}>
                <ThumbnailCanvasWrapper ref={canvasRef} />
                <ThumbnailNumberWrapper>{pageNumber}</ThumbnailNumberWrapper>
            </ThumbnailContainer>
        </ThumbnailWrapper>
    );
};
